customElements.define('cart-premium', class CartPremium extends HTMLElement {
  connectedCallback() {
    this.moneyFormat = this.dataset.moneyFormat || '€{{ amount_with_comma_separator }}';
    this.deliveryFee = Number(this.dataset.deliveryFee || 0);
    this.freeShippingThreshold = Number(this.dataset.freeShippingThreshold || 20000);
    this.bindLines();
    this.bindDelivery();
    this.bindCheckout();
  }

  bindLines() {
    this.querySelectorAll('[data-cart-line]').forEach((line) => {
      line.querySelector('[data-qty-minus]')?.addEventListener('click', () => {
        const input = line.querySelector('[data-qty-input]');
        this.changeLine(line, Math.max(0, Number(input.value || 0) - 1));
      });

      line.querySelector('[data-qty-plus]')?.addEventListener('click', () => {
        const input = line.querySelector('[data-qty-input]');
        this.changeLine(line, Number(input.value || 0) + 1);
      });

      line.querySelector('[data-qty-input]')?.addEventListener('change', (event) => {
        this.changeLine(line, Math.max(0, Number(event.target.value || 0)));
      });

      line.querySelector('[data-remove-line]')?.addEventListener('click', () => {
        this.changeLine(line, 0);
      });
    });
  }

  bindDelivery() {
    this.querySelectorAll('[data-delivery-mode]').forEach((input) => {
      input.addEventListener('change', () => {
        this.querySelectorAll('.cart-shipping label').forEach((label) => label.classList.remove('is-selected'));
        input.closest('label')?.classList.add('is-selected');
        this.updateAttributes();
        this.refreshTotalsFromDom();
      });
    });

    this.querySelector('[data-delivery-date]')?.addEventListener('change', () => this.updateAttributes());
  }

  bindCheckout() {
    this.querySelector('#CartCheckoutForm')?.addEventListener('submit', async (event) => {
      const form = event.currentTarget;
      const button = form.querySelector('[data-checkout-button]');
      if (form.dataset.readyForCheckout === 'true') return;
      event.preventDefault();
      button.classList.add('is-loading');
      await this.updateAttributes();
      form.dataset.readyForCheckout = 'true';
      form.requestSubmit(button);
    });
  }

  async changeLine(line, quantity) {
    line.classList.add('is-updating');
    const response = await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        id: line.dataset.lineKey,
        quantity
      })
    });

    const cart = await response.json();
    this.renderCart(cart);
  }

  async updateAttributes() {
    const mode = this.querySelector('[data-delivery-mode]:checked')?.value || '';
    const date = this.querySelector('[data-delivery-date]')?.value || '';

    await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        attributes: {
          'Mode de livraison': mode,
          'Date de livraison': date
        }
      })
    });
  }

  renderCart(cart) {
    cart.items.forEach((item) => {
      const line = this.querySelector(`[data-line-key="${CSS.escape(item.key)}"]`);
      if (!line) return;
      line.querySelector('[data-qty-input]').value = item.quantity;
      line.querySelector('[data-line-price]').textContent = this.formatMoney(item.final_price);
      line.querySelector('[data-line-total]').textContent = this.formatMoney(item.final_line_price);
      line.classList.remove('is-updating');
    });

    this.querySelectorAll('[data-cart-line]').forEach((line) => {
      const exists = cart.items.some((item) => item.key === line.dataset.lineKey);
      if (!exists) line.remove();
    });

    this.updateTotals(cart);
    this.updateCartCount(cart.item_count);

    if (cart.item_count === 0) {
      window.location.reload();
    }
  }

  updateTotals(cart) {
    const delivery = this.currentDeliveryFee(cart);
    const total = cart.total_price + delivery;
    const discountRow = this.querySelector('[data-discount-row]');

    this.querySelector('[data-cart-subtotal]').textContent = this.formatMoney(cart.items_subtotal_price);
    this.querySelector('[data-cart-delivery]').textContent = delivery > 0 ? this.formatMoney(delivery) : 'Gratuit';
    this.querySelector('[data-cart-total]').textContent = this.formatMoney(total);
    this.querySelector('[data-home-delivery-price]').textContent = cart.total_price >= this.freeShippingThreshold
      ? 'Offerte'
      : this.formatMoney(this.deliveryFee);
    this.updateFreeShipping(cart);

    if (discountRow) {
      discountRow.hidden = cart.total_discount <= 0;
      this.querySelector('[data-cart-discount]').textContent = `-${this.formatMoney(cart.total_discount)}`;
    }
  }

  refreshTotalsFromDom() {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => this.updateTotals(cart));
  }

  currentDeliveryFee(cart) {
    const mode = this.querySelector('[data-delivery-mode]:checked')?.value || '';
    if (mode.includes('Retrait')) return 0;
    if (cart && cart.total_price >= this.freeShippingThreshold) return 0;
    return this.deliveryFee;
  }

  updateFreeShipping(cart) {
    const root = this.querySelector('[data-free-shipping]');
    if (!root || !cart) return;

    const remaining = Math.max(this.freeShippingThreshold - cart.total_price, 0);
    const progress = this.freeShippingThreshold > 0
      ? Math.min(Math.round((cart.total_price / this.freeShippingThreshold) * 100), 100)
      : 100;
    const complete = remaining === 0;

    root.classList.toggle('is-complete', complete);
    root.querySelector('[data-free-shipping-title]').textContent = complete
      ? 'Livraison offerte'
      : 'Livraison offerte dès 200 €';
    root.querySelector('[data-free-shipping-message]').textContent = complete
      ? 'Félicitations, votre livraison est offerte.'
      : `Plus que ${this.formatMoney(remaining)} pour profiter de la livraison offerte.`;
    const bar = root.querySelector('[data-free-shipping-bar]');
    if (bar) bar.style.width = `${progress}%`;
    const track = root.querySelector('[role="progressbar"]');
    if (track) track.setAttribute('aria-valuenow', String(progress));
  }

  updateCartCount(count) {
    document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"], .cart-count-bubble .cart-count').forEach((node) => {
      node.textContent = count;
    });
  }

  formatMoney(cents) {
    const amount = (Number(cents || 0) / 100).toFixed(2);
    const comma = amount.replace('.', ',');
    return this.moneyFormat
      .replace('{{amount_with_comma_separator}}', comma)
      .replace('{{ amount_with_comma_separator }}', comma)
      .replace('{{amount}}', amount)
      .replace('{{ amount }}', amount);
  }
});
